/**
 * Super admin — manage organisations (deactivate for terms violations).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

export default function SuperAdminDash() {
  const { signOutUser } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError('');
    const { data, error: err } = await supabase
      .from('organizations')
      .select('id, name, slug, city, country, org_type, active, created_at')
      .order('name');

    if (err) {
      setError(err.message);
      return;
    }
    setOrgs(data || []);
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  async function toggleOrgActive(org) {
    setError('');
    setBusyId(org.id);
    const { error: err } = await supabase.rpc('set_organization_active', {
      p_org_id: org.id,
      p_active: !org.active,
    });
    setBusyId(null);
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  async function handleSignOut() {
    await signOutUser();
    navigate('/login', { replace: true });
  }

  if (loading) {
    return (
      <div style={shell}>
        <p style={{ color: muted }}>Loading super admin…</p>
      </div>
    );
  }

  return (
    <div style={shell}>
      <header style={headerBar}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>Super Admin</h1>
          <p style={{ margin: '4px 0 0', color: muted, fontSize: '0.9rem' }}>
            Platform organisations — deactivate if terms are violated
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" onClick={() => navigate('/admin')} style={ghostBtn}>
            Org admin
          </button>
          <button type="button" onClick={handleSignOut} style={ghostBtn}>
            Sign out
          </button>
        </div>
      </header>

      {error ? (
        <p style={{ color: '#f28b82', marginBottom: 12 }} role="alert">
          {error}
        </p>
      ) : null}

      <section style={section}>
        <h2 style={h2}>Organisations ({orgs.length})</h2>
        {orgs.length === 0 ? (
          <p style={{ color: muted }}>No organisations registered yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orgs.map((org) => (
              <div key={org.id} style={cardBox}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '1.05rem' }}>{org.name}</strong>
                    <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                      {org.slug} · {org.city || '—'}, {org.country || '—'}
                      {org.org_type ? ` · ${org.org_type}` : ''}
                    </div>
                    <div style={{ marginTop: 6, fontSize: '0.88rem' }}>
                      Status:{' '}
                      <span style={{ color: org.active !== false ? '#81c995' : '#f28b82', fontWeight: 700 }}>
                        {org.active !== false ? 'Active' : 'Deactivated'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleOrgActive(org)}
                    disabled={busyId === org.id}
                    style={org.active !== false ? dangerBtn : primaryBtn}
                  >
                    {busyId === org.id ? 'Working…' : org.active !== false ? 'Deactivate org' : 'Reactivate org'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: bg,
  color: text,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  padding: '20px 16px 40px',
  maxWidth: 960,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const headerBar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const section = { marginBottom: 28 };
const h2 = { fontSize: '1.15rem', margin: '0 0 12px', color: text };

const cardBox = {
  background: card,
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.06)',
};

const primaryBtn = {
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
};

const dangerBtn = {
  ...ghostBtn,
  borderColor: 'rgba(242,139,130,0.5)',
  color: '#f28b82',
};
